import { FormEvent, useState } from "react"
import { useForm } from "@inertiajs/react"
import { Package, Plus, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { FieldError } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  PURCHASING_PATHS,
  packQuantity,
  packUnitCost,
  purchaseDraftTotal,
  purchaseRequestComplete,
  lineTotal,
  tPurchasing,
  type Supplier,
  type SupplyOption,
} from "@/features/staff/purchasing/purchasing"
import { formatMoney } from "@/lib/formatters"

export type PurchaseFormProps = {
  suppliers: Supplier[]
  supplies: SupplyOption[]
  currency: string
}

type LineDraft = {
  key: number
  supplyId: string
  quantity: string
  unitCost: string
  /** Entry helper only. Nothing about the pack is posted or stored. */
  packMode: boolean
  packs: string
  unitsPerPack: string
  packPrice: string
}

function emptyLine(key: number): LineDraft {
  return {
    key,
    supplyId: "",
    quantity: "",
    unitCost: "",
    packMode: false,
    packs: "",
    unitsPerPack: "",
    packPrice: "",
  }
}

/**
 * Writing a purchase order.
 *
 * ## The unit beside the quantity field is the whole defence
 *
 * A line's quantity is denominated in the **supply's** unit and no conversion
 * exists anywhere in this feature — `PurchaseLine#apply_to_stock!` hands the
 * figure straight to the ledger. So the unit is rendered against every quantity
 * input, and the pack helper does the multiplication a manager would otherwise
 * do in their head: "3 costales de 20000 g a $240" becomes 60000 g at 0.012 per
 * gram. Get that wrong and the shortage report is wrong by a factor of a
 * thousand, in the direction that reads as plenty.
 *
 * The pack figures are never posted. Storing them would be the first half of a
 * conversion mechanism the schema deliberately does not have, and the second
 * half is what makes an inventory add kilos to grams.
 *
 * ## An explicit submit, and no `status` field
 *
 * Supplier, invoice and lines only mean anything together, which is the
 * "Updates vs creates" rule's own example. A purchase is always created as a
 * draft: receiving is a separate, deliberate act on its own screen, because it
 * is the act that moves stock and repoints every average cost.
 */
export function PurchaseForm({ suppliers, supplies, currency }: PurchaseFormProps) {
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(0)])
  const [nextKey, setNextKey] = useState(1)

  const { data, setData, post, transform, errors, processing } = useForm({
    supplier_id: "",
    invoice_number: "",
  })

  // `record.errors.to_hash(true)` keys a model-level refusal under `base`, and
  // an associated-record failure under `lines.quantity`. Neither is a field on
  // this form, so neither is in `useForm`'s inferred error map — dropping them
  // would leave a manager watching a save do nothing.
  const errorBag = errors as Record<string, string | string[] | undefined>
  const baseError = errorBag.base
  const lineError = errorBag["lines.quantity"] ?? errorBag["lines.unit_cost"] ?? errorBag.lines

  const supplyById = new Map(supplies.map((supply) => [String(supply.id), supply]))

  const complete = purchaseRequestComplete({
    supplierId: data.supplier_id,
    lines: lines.map((line) => ({
      supplyId: line.supplyId,
      quantity: line.quantity,
      unitCost: line.unitCost,
    })),
  })

  const updateLine = (key: number, patch: Partial<LineDraft>) => {
    setLines((current) =>
      current.map((line) => {
        if (line.key !== key) return line

        const next = { ...line, ...patch }

        // The helper writes into the same two fields the endpoint reads, so
        // switching the checkbox off leaves the computed figures in place and
        // editable rather than discarding what was just worked out.
        if (next.packMode) {
          next.quantity = packQuantity(next.packs, next.unitsPerPack) || next.quantity
          next.unitCost = packUnitCost(next.packPrice, next.unitsPerPack) || next.unitCost
        }

        return next
      })
    )
  }

  const addLine = () => {
    setLines((current) => [...current, emptyLine(nextKey)])
    setNextKey((key) => key + 1)
  }

  const removeLine = (key: number) => {
    setLines((current) => (current.length === 1 ? current : current.filter((l) => l.key !== key)))
  }

  const total = purchaseDraftTotal(
    lines.map((line) => ({
      supplyId: line.supplyId,
      quantity: line.quantity,
      unitCost: line.unitCost,
    }))
  )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!complete || processing) return

    transform((current) => ({
      ...current,
      lines: lines.map((line) => ({
        supply_id: line.supplyId,
        quantity: line.quantity,
        unit_cost: line.unitCost,
      })),
    }))

    post(PURCHASING_PATHS.createPurchase, { preserveScroll: true })
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="purchase-new-form">
      <FieldError error={baseError} />
      <FieldError error={lineError} />

      <Card density="compact">
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="purchase-supplier">{tPurchasing("purchase_supplier_label")}</Label>
            <Select
              value={data.supplier_id}
              onValueChange={(value) => setData("supplier_id", value)}
            >
              <SelectTrigger id="purchase-supplier" className="w-full" data-testid="purchase-supplier">
                <SelectValue placeholder={tPurchasing("purchase_supplier_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((supplier) => (
                  <SelectItem
                    key={supplier.id}
                    value={String(supplier.id)}
                    data-testid={`purchase-supplier-option-${supplier.id}`}
                  >
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError error={errorBag.supplier} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="purchase-invoice">{tPurchasing("purchase_invoice_label")}</Label>
            <Input
              id="purchase-invoice"
              value={data.invoice_number}
              onChange={(event) => setData("invoice_number", event.target.value)}
              placeholder={tPurchasing("purchase_invoice_placeholder")}
              data-testid="purchase-invoice"
            />
            <p className="text-muted-foreground text-xs">{tPurchasing("purchase_invoice_hint")}</p>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4" data-testid="purchase-lines">
        {lines.map((line, index) => {
          const supply = supplyById.get(line.supplyId)
          const unitLabel = supply?.unitLabel ?? tPurchasing("line_unit_unknown")

          return (
            <Card density="compact" key={line.key} data-testid={`purchase-line-${index}`}>
              <CardContent className="space-y-4">
                <div className="flex items-end gap-2">
                  <div className="min-w-0 flex-1 space-y-2">
                    <Label htmlFor={`purchase-line-${index}-supply`}>
                      {tPurchasing("line_supply_label")}
                    </Label>
                    <Select
                      value={line.supplyId}
                      onValueChange={(value) => updateLine(line.key, { supplyId: value })}
                    >
                      <SelectTrigger
                        id={`purchase-line-${index}-supply`}
                        className="w-full"
                        data-testid={`purchase-line-${index}-supply`}
                      >
                        <SelectValue placeholder={tPurchasing("line_supply_placeholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {supplies.map((option) => (
                          <SelectItem
                            key={option.id}
                            value={String(option.id)}
                            data-testid={`purchase-line-${index}-supply-option-${option.id}`}
                          >
                            {option.name} · {option.unitLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    disabled={lines.length === 1}
                    onClick={() => removeLine(line.key)}
                    aria-label={tPurchasing("line_remove")}
                    data-testid={`purchase-line-${index}-remove`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`purchase-line-${index}-pack-mode`}
                    checked={line.packMode}
                    onCheckedChange={(checked) =>
                      updateLine(line.key, { packMode: checked === true })
                    }
                    data-testid={`purchase-line-${index}-pack-mode`}
                  />
                  <Label
                    htmlFor={`purchase-line-${index}-pack-mode`}
                    className="text-muted-foreground text-xs font-normal"
                  >
                    {tPurchasing("line_pack_mode")}
                  </Label>
                </div>

                {line.packMode && (
                  <div className="border-border grid gap-4 border-t pt-4 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor={`purchase-line-${index}-packs`}>
                        {tPurchasing("line_packs_label")}
                      </Label>
                      <Input
                        id={`purchase-line-${index}-packs`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="1"
                        value={line.packs}
                        onChange={(event) => updateLine(line.key, { packs: event.target.value })}
                        className="tabular-nums"
                        data-testid={`purchase-line-${index}-packs`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`purchase-line-${index}-units-per-pack`}>
                        {tPurchasing("line_units_per_pack_label", { unit: unitLabel })}
                      </Label>
                      <Input
                        id={`purchase-line-${index}-units-per-pack`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.001"
                        value={line.unitsPerPack}
                        onChange={(event) =>
                          updateLine(line.key, { unitsPerPack: event.target.value })
                        }
                        className="tabular-nums"
                        data-testid={`purchase-line-${index}-units-per-pack`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`purchase-line-${index}-pack-price`}>
                        {tPurchasing("line_pack_price_label")}
                      </Label>
                      <Input
                        id={`purchase-line-${index}-pack-price`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={line.packPrice}
                        onChange={(event) =>
                          updateLine(line.key, { packPrice: event.target.value })
                        }
                        className="tabular-nums"
                        data-testid={`purchase-line-${index}-pack-price`}
                      />
                    </div>

                    <p className="text-muted-foreground text-xs sm:col-span-3">
                      {tPurchasing("line_pack_hint", { unit: unitLabel })}
                    </p>
                  </div>
                )}

                <div className="border-border grid gap-4 border-t pt-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor={`purchase-line-${index}-quantity`}>
                      {tPurchasing("line_quantity_label")}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id={`purchase-line-${index}-quantity`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.001"
                        value={line.quantity}
                        onChange={(event) =>
                          updateLine(line.key, { quantity: event.target.value })
                        }
                        className="tabular-nums"
                        data-testid={`purchase-line-${index}-quantity`}
                        required
                      />
                      <span
                        className="text-muted-foreground shrink-0 text-xs"
                        data-testid={`purchase-line-${index}-unit`}
                      >
                        {unitLabel}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`purchase-line-${index}-unit-cost`}>
                      {tPurchasing("line_unit_cost_label", { unit: unitLabel })}
                    </Label>
                    <Input
                      id={`purchase-line-${index}-unit-cost`}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.0001"
                      value={line.unitCost}
                      onChange={(event) => updateLine(line.key, { unitCost: event.target.value })}
                      className="tabular-nums"
                      data-testid={`purchase-line-${index}-unit-cost`}
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs">
                      {tPurchasing("line_total_label")}
                    </p>
                    <p
                      className="text-foreground text-sm font-semibold tabular-nums"
                      data-testid={`purchase-line-${index}-total`}
                    >
                      {formatMoney(lineTotal(line.quantity, line.unitCost), { currency })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <Button type="button" variant="outline" onClick={addLine} data-testid="purchase-line-add">
          <Plus className="size-4" />
          {tPurchasing("line_add")}
        </Button>

        <div className="space-y-1 text-right">
          <p className="text-muted-foreground text-xs">{tPurchasing("purchase_total_label")}</p>
          <p
            className="text-foreground text-sm font-semibold tabular-nums"
            data-testid="purchase-total"
          >
            {formatMoney(total, { currency })}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <p className="text-muted-foreground mr-auto flex items-center gap-2 text-xs">
          <Package className="size-4 shrink-0" aria-hidden="true" />
          {tPurchasing("purchase_draft_hint")}
        </p>
        <Button type="submit" disabled={processing || !complete} data-testid="purchase-submit">
          {tPurchasing("purchase_create")}
        </Button>
      </div>
    </form>
  )
}
